import cloneDeep from 'lodash/cloneDeep';
import { array, bool, func, number, oneOfType, string } from 'prop-types';
import _find from 'lodash/find';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Accordion from 'ravenjs/lib/Accordion';
import { callFunc } from 'ravenjs/utils/actions';
import Typography from 'ravenjs/lib/Typography';
import Box from 'ravenjs/lib/Box';
import Checkbox from 'ravenjs/lib/CheckboxNew';
import { renderCheck } from 'ravenjs/utils/viewport';

import { CheckboxEmptyIcon, CheckboxFilledIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import HeaderComponent from './HeaderComponent';

const AccordionStyled = styled(Accordion)`
    border: none;
    border-radius: 0;
    box-shadow: none;
`;

const CheckboxItem = styled.div`
    align-items: start;
    display: flex;
    padding-top: 1.268rem;
    overflow-x: hidden;
    width: 100%;
    word-break: break-word;
`;

const ContentComponent = styled.div`
    background: ${DEFAULT_COLORS.WHITE};
    padding: 0 0.714rem;
`;

const DualColumnCheckboxItem = styled.div`
    align-items: start;
    display: flex;
    overflow-x: hidden;
    padding-top: 0.63rem;
    width: 100%;
    word-break: break-word;
`;

const DualColumnContainer = styled.div`
    display: flex;
    padding: 0.5rem 0 0 0;
`;

const DualColumnColumn = styled.div`
    padding: 0 0 0 0.5rem;
    width: 50%;
`;

const IdentifierPatch = styled.div`
    background: ${({ color }) => color || 'transparent'};
    display: flex;
    height: 0.875rem;
    width: 0.875rem;
    border-radius: 100px;
`;

const ListFilters = styled.div`
    display: flex;
    width: 100%;
    position: relative;
    padding: 0.375rem 0;
    align-items: center;
    justify-content: space-between;
`;

const ListFiltersData = styled.div`
    display: flex;
    align-items: center;
`;

const ListFiltersIcon = styled.div`
    margin-right: 0.625rem;
    min-width: 0.9375rem;
    line-height: 0.75;
`;

const ListFiltersItems = styled(Typography)`
    text-transform: uppercase;
`;

const ListFiltersCheck = styled.div`
    line-height: 0.75;
`;

const CheckboxFilter = props => {
    const {
        checkAllOptions,
        excludeSelected,
        filterIdentifier,
        id,
        isMultipleSelection,
        name,
        onChange,
        options,
        showDualColumnCheckboxes,
        showExternalCheckboxes,
        title,
        usesSubcategories,
        value: filterValues,
    } = props;
    const isTouchDevice = 'ontouchstart' in window || navigator.msMaxTouchPoints;
    const newValues = cloneDeep(filterValues);
    const [isOpen, setIsOpen] = useState(false);
    const [count, setCount] = useState(null);
    const [currentSelected, setCurrentSelected] = useState(filterValues);
    const handleHeaderClick = () => {
        if (renderCheck('md', 'less') || isTouchDevice) {
            if (isOpen) {
                setIsOpen(false);
            } else {
                setIsOpen(true);
            }
        }
    };

    useEffect(() => {
        setCurrentSelected(filterValues);
    }, [filterValues]);

    const headerProps = {
        checkAllOptions,
        count,
        currentSelected,
        excludeSelected,
        isOpen,
        onHeaderClick: handleHeaderClick,
        title,
        useAltIcon: showDualColumnCheckboxes,
    };

    React.useEffect(() => {
        if (!_isEqual(_sortBy(newValues), _sortBy(currentSelected))) {
            if (currentSelected[0] && currentSelected[0].toString().indexOf('All') === -1) {
                setCurrentSelected(newValues);
            }

            if (checkAllOptions) {
                setCurrentSelected(newValues);
            }

            if (
                currentSelected[0] &&
                currentSelected[0].toString().indexOf('All') !== -1 &&
                currentSelected.length === 1
            ) {
                setCurrentSelected(newValues);
            }
        }

        if (checkAllOptions) {
            let count = 0;

            if (currentSelected.length === options.length) {
                count = currentSelected.length - 1;
            } else {
                count = currentSelected.length;
            }
            setCount(count);
        }
    }, [checkAllOptions, currentSelected, newValues, options, usesSubcategories]);

    const handleOnChange = inputId => {
        const allOptionId = options[0].id;
        const optionsWithoutAll = options
            .filter(item => item.id !== allOptionId)
            .map(item => item.id);
        let selectedValues = cloneDeep(filterValues);

        if (!isMultipleSelection) {
            if (selectedValues.indexOf(inputId) !== -1) {
                selectedValues = [];
            } else {
                selectedValues = [inputId];
            }
        } else if (usesSubcategories) {
            const changedOption = _find(options, { id: inputId });
            if (changedOption) {
                if (changedOption.id === allOptionId) {
                    // clicked All button
                    selectedValues = [allOptionId];
                } else if (changedOption.subcategories) {
                    // clicked a parent category
                    // if not previously selected
                    if (filterValues.indexOf(inputId) === -1) {
                        // add its id
                        selectedValues = [...selectedValues, inputId];
                        // add its subcategories' ids
                        changedOption.subcategories.forEach(({ id: subcategoryId }) => {
                            if (selectedValues.indexOf(subcategoryId) === -1) {
                                selectedValues = [...selectedValues, subcategoryId];
                            }
                        });
                    } else {
                        // parent category now unselected
                        // remove its id
                        selectedValues = selectedValues.filter(
                            selectedValue => selectedValue !== inputId
                        );
                        // remove its subcategories' ids
                        changedOption.subcategories.forEach(({ id: subcategoryId }) => {
                            selectedValues = selectedValues.filter(
                                selectedValue => selectedValue !== subcategoryId
                            );
                        });
                    }
                } else if (changedOption.parentId) {
                    // clicked a subcategory item
                    if (filterValues.indexOf(inputId) === -1) {
                        selectedValues = [...selectedValues, inputId];
                    } else {
                        selectedValues = selectedValues.filter(
                            selectedValue => selectedValue !== inputId
                        );
                    }
                    // select or unselect parent
                    const parentOption = _find(options, { id: changedOption.parentId });
                    let allSubcategoriesSelected = true;
                    parentOption.subcategories.forEach(({ id }) => {
                        allSubcategoriesSelected =
                            allSubcategoriesSelected && selectedValues.indexOf(id) > -1;
                    });
                    if (allSubcategoriesSelected && filterValues.indexOf(parentOption.id) === -1) {
                        selectedValues = [...selectedValues, parentOption.id];
                    } else {
                        selectedValues = selectedValues.filter(
                            selectedValue => selectedValue !== parentOption.id
                        );
                    }
                }

                // select or unselect All when other options have changed
                if (
                    changedOption.id !== allOptionId &&
                    filterValues.indexOf(allOptionId) === -1 &&
                    (selectedValues.length === options.length - 1 || selectedValues.length === 0)
                ) {
                    // select All option
                    selectedValues = [allOptionId];
                } else if (
                    changedOption.id !== allOptionId &&
                    filterValues.indexOf(allOptionId) > -1 &&
                    selectedValues.length < options.length - 1
                ) {
                    // unselect All option
                    selectedValues = selectedValues.filter(
                        selectedValue => selectedValue !== allOptionId
                    );
                }
            }
        } else if (checkAllOptions) {
            if (inputId === allOptionId) {
                if (currentSelected.indexOf(allOptionId) === -1) {
                    selectedValues = options.map(item => item.id);
                } else {
                    selectedValues = [];
                }
            } else if (filterValues.indexOf(inputId) === -1) {
                selectedValues = [...selectedValues, inputId];
            } else {
                selectedValues = selectedValues.filter(selectedValue => selectedValue !== inputId);
                selectedValues = selectedValues.filter(
                    selectedValue => selectedValue !== allOptionId
                );
            }

            if (
                selectedValues.length === options.length ||
                _isEqual(_sortBy(optionsWithoutAll), _sortBy(selectedValues))
            ) {
                selectedValues = options.map(item => item.id);
            }
        } else if (inputId !== allOptionId || filterValues.indexOf(allOptionId) === -1) {
            if (filterValues.indexOf(inputId) === -1) {
                selectedValues = [...selectedValues, inputId];
            } else {
                selectedValues = selectedValues.filter(selectedValue => selectedValue !== inputId);
            }

            if (
                filterValues.indexOf(allOptionId) === -1 &&
                (selectedValues.length === options.length - 1 || _isEmpty(selectedValues))
            ) {
                selectedValues = [allOptionId];
            }
        }

        setCurrentSelected(selectedValues);
        callFunc(onChange, name, selectedValues, true);
    };

    const handleOnMouseEnter = () => {
        if (renderCheck('md', 'greater') && !isTouchDevice) {
            setIsOpen(true);
        }
    };

    const handleOnMouseLeave = () => {
        if (renderCheck('md', 'greater')) {
            setIsOpen(false);
            if (showExternalCheckboxes) {
                callFunc(onChange, name, currentSelected);
            }
        }
    };

    const renderCheckboxes = () => {
        return options.map(({ color, id: inputId, label }) => {
            const onInputChange = () => {
                handleOnChange(inputId);
            };

            return (
                <CheckboxItem key={inputId}>
                    <Checkbox
                        checked={filterValues.indexOf(inputId) !== -1}
                        id={inputId}
                        type="checkbox"
                        icon={CheckboxEmptyIcon}
                        checkedIcon={CheckboxFilledIcon}
                        onChange={onInputChange}
                    />
                    <Typography as="label" htmlFor={inputId} type="documentCheckboxFilter">
                        {label}
                    </Typography>
                    {color && <IdentifierPatch color={color} />}
                </CheckboxItem>
            );
        });
    };

    const renderDualColumnCheckboxes = () => {
        const optionsHalfLength = Math.ceil(options.length / 2);
        const optionsColOne = options.slice(0, optionsHalfLength);
        const optionsColTwo = options.slice(optionsHalfLength);

        const getColumnItems = options =>
            options.map(({ id: inputId, label }) => {
                const onInputChange = () => {
                    handleOnChange(inputId);
                };

                return (
                    <DualColumnCheckboxItem key={inputId}>
                        <Checkbox
                            checked={filterValues.indexOf(inputId) !== -1}
                            id={inputId}
                            type="checkbox"
                            icon={CheckboxEmptyIcon}
                            checkedIcon={CheckboxFilledIcon}
                            onChange={onInputChange}
                        />
                        <Typography as="label" htmlFor={inputId} type="documentCheckboxFilter">
                            {label}
                        </Typography>
                    </DualColumnCheckboxItem>
                );
            });
        const itemsColOne = getColumnItems(optionsColOne);
        const itemsColTwo = getColumnItems(optionsColTwo);

        return (
            <DualColumnContainer>
                <DualColumnColumn>{itemsColOne}</DualColumnColumn>
                <DualColumnColumn>{itemsColTwo}</DualColumnColumn>
            </DualColumnContainer>
        );
    };

    const renderFilterIdentifier = (type, color, icon) => {
        let identifier = '';

        switch (type) {
            case 'color':
                identifier = color ? <IdentifierPatch color={color} /> : ' ';
                break;

            case 'icon':
                identifier = icon;
                break;

            default:
                identifier = '';
                break;
        }

        return identifier;
    };

    const renderExternalCheckboxes = () => {
        const needScroll = options.length > 10;
        return (
            <Box
                borderColor="transparent"
                backgroundColor="transparent"
                padding={needScroll ? '0.875rem 0.25rem 0 0' : '0.875rem 0 0 0'}
                height={needScroll ? '21.875rem' : undefined}
                overflow={needScroll ? 'hidden scroll' : undefined}
            >
                {options.map(({ color, icon, id: inputId, label }) => {
                    const onInputChange = () => {
                        handleOnChange(inputId);
                    };

                    return (
                        <ListFilters key={inputId}>
                            <ListFiltersData>
                                {filterIdentifier && (
                                    <ListFiltersIcon>
                                        {renderFilterIdentifier(filterIdentifier, color, icon)}
                                    </ListFiltersIcon>
                                )}
                                <ListFiltersItems
                                    fontFamily="Roboto"
                                    fontSize="0.75rem"
                                    fontWeight="500"
                                    gutterTop="0"
                                    gutterBottom="0"
                                    color={DEFAULT_COLORS.BLACK}
                                >
                                    {label}
                                </ListFiltersItems>
                            </ListFiltersData>
                            <ListFiltersCheck>
                                <Checkbox
                                    checked={
                                        checkAllOptions
                                            ? currentSelected.indexOf(inputId) !== -1
                                            : filterValues.indexOf(inputId) !== -1
                                    }
                                    id={inputId}
                                    type="checkbox"
                                    icon={CheckboxEmptyIcon}
                                    checkedIcon={CheckboxFilledIcon}
                                    onChange={onInputChange}
                                />
                            </ListFiltersCheck>
                        </ListFilters>
                    );
                })}
            </Box>
        );
    };

    return (
        <div id={id} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
            <AccordionStyled
                expanded={isOpen}
                contentComponent={ContentComponent}
                headerComponent={HeaderComponent}
                HeaderProps={headerProps}
            >
                {!showExternalCheckboxes && !showDualColumnCheckboxes && renderCheckboxes()}
                {!showExternalCheckboxes &&
                    showDualColumnCheckboxes &&
                    renderDualColumnCheckboxes()}
            </AccordionStyled>
            {showExternalCheckboxes &&
                !showDualColumnCheckboxes &&
                isOpen &&
                renderExternalCheckboxes()}
            {showExternalCheckboxes &&
                showDualColumnCheckboxes &&
                isOpen &&
                renderDualColumnCheckboxes()}
        </div>
    );
};

CheckboxFilter.propTypes = {
    checkAllOptions: bool,
    excludeSelected: array,
    filterIdentifier: string,
    id: oneOfType([number, string]).isRequired,
    isMultipleSelection: bool,
    name: string.isRequired,
    onChange: func,
    options: array,
    showDualColumnCheckboxes: bool,
    showExternalCheckboxes: bool,
    title: string,
    usesSubcategories: bool,
    value: array,
};

CheckboxFilter.defaultProps = {
    checkAllOptions: false,
    excludeSelected: [],
    filterIdentifier: '',
    isMultipleSelection: true,
    onChange: null,
    options: [],
    showDualColumnCheckboxes: false,
    showExternalCheckboxes: false,
    title: null,
    usesSubcategories: false,
    value: [],
};

export { CheckboxFilter as CheckboxFilterUnwrapped };
export default CheckboxFilter;
